import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageContent } from '../hooks/usePageContent';
import { useProjects } from '../hooks/useProjects';

const DEFAULT_HERO = {
    eyebrow: 'Open to Product Manager roles · Toronto / Canada / Remote',
    heading: 'Building AI products that earn their place in someone\'s day.',
    description: 'Product Manager with Big Data Analytics Background. Five years shipping ML products across Southeast Asia. Most recently at Carro, SEA\'s $3B used-car marketplace.',
};

const DEFAULT_WORK = {
    eyebrow: 'Selected work',
    heading: 'Recent case studies.',
};

const DEFAULT_SITE = {
    name: 'Chaw Thinn',
    title: 'Product Manager',
};

const DEFAULT_CAPABILITIES_SECTION = {
    eyebrow: 'Core competencies',
    heading: 'Turning Ambiguity into Scalable Solutions.',
};

const DEFAULT_CAPABILITIES = [
    {
        year: '2021–24',
        company: 'Carro · Singapore',
        title: 'AI-driven pricing engine cut manual quotation time by 60% across 6 markets',
        summary:
            'At SEA’s largest used-car marketplace ($3B unicorn), I owned the ML valuation tool that replaced dealer-priced workflows — built on transaction data, demand signals, and market-specific rules. Defined accuracy and coverage metrics, training-data quality checks, and A/B tests to monitor drift and post-launch lift.',
        tags: ['ML', 'MLOps', 'Marketplace'],
        href: '/work',
    },
    {
        year: '2022',
        company: 'Carro · Taiwan launch',
        title: 'Launched Carro Taiwan end-to-end — 15% lead conversion in Q1',
        summary:
            'Owned seller onboarding, product, and payments localization for a new market. Partnered with engineering, data, and ops to take a regional playbook and re-shape it for local buyers and dealers.',
        tags: ['0→1', 'GTM', 'Localization'],
        href: '/work',
    },
    {
        year: '2020–21',
        company: 'Citizens Wallet · Myanmar',
        title: 'Scaled a nationwide mobile wallet to 10K+ monthly transactions in Q1',
        summary:
            'Launched across all 5 Myanmar telcos with CDSG. Prioritized high-frequency use cases, optimized onboarding, and lifted referral conversion 19% via cohort and segmentation analysis. Onboarded 50+ partner APIs.',
        tags: ['Fintech', 'Growth', 'Cohort analysis'],
        href: '/work',
    },
];

const CAPABILITIES = [
    {
        label: 'AI/ML product strategy',
        body: 'Scoping the right ML feature, picking the right model surface, and writing PRDs that data scientists and engineers actually want to build.',
    },
    {
        label: 'Model evaluation & MLOps',
        body: 'Accuracy and coverage metrics, training-data quality checks, A/B tests for drift and post-launch lift. Treating evals as the spec, not the afterthought.',
    },
    {
        label: 'Experimentation & analytics',
        body: 'A/B and multivariate experiments, funnel and cohort analysis, event tracking and data modeling on Snowflake, BigQuery, and Databricks.',
    },
    {
        label: 'Cross-market GTM',
        body: 'Shipped products across SG, MY, ID, TH, TW, JP, and Myanmar. Comfortable with localization, partner integrations, and multi-stakeholder rollouts.',
    },
];

const DEFAULT_ABOUT_TEASER = {
    eyebrow: 'About',
    description: 'Engineer-trained, MBA-finished, PMP- and PSPO-certified. I think in funnels and feature stores, and I care about getting ML products from demo to dependable.',
};

const DEFAULT_CONTACT_BAND = {
    eyebrow: 'Get in touch',
    heading: 'Have an AI product worth getting right?',
    email: 'chawthinn1@gmail.com',
    linkedin: 'https://linkedin.com/in/chawthinn',
};

export default function Home() {
    const { content } = usePageContent('home');
    const { content: contactContent } = usePageContent('contact');
    const { content: siteContent } = usePageContent('site');
    const { projects } = useProjects();
    const [homeContent, setHomeContent] = useState(null);
    const [contactBandData, setContactBandData] = useState(null);

    useEffect(() => {
        const site = siteContent || DEFAULT_SITE;
        document.title = `${site.name || DEFAULT_SITE.name}: ${site.title || DEFAULT_SITE.title}`;
    }, [siteContent]);

    useEffect(() => {
        if (content) {
            setHomeContent(content);
        }
    }, [content]);

    useEffect(() => {
        if (contactContent) {
            // Extract email and LinkedIn from contact channels
            const channels = contactContent.channels || [];
            const emailChannel = channels.find(c => c.label?.toLowerCase() === 'email');
            const linkedinChannel = channels.find(c => c.label?.toLowerCase() === 'linkedin');

            setContactBandData({
                email: emailChannel?.value || DEFAULT_CONTACT_BAND.email,
                linkedin: linkedinChannel?.href || DEFAULT_CONTACT_BAND.linkedin,
            });
        }
    }, [contactContent]);

    const hero = homeContent?.hero || { eyebrow: '', heading: '', description: '' };
    const work = homeContent?.work || { eyebrow: '', heading: '' };
    const capabilitiesSection = homeContent?.capabilitiesSection || DEFAULT_CAPABILITIES_SECTION;
    const capabilities = homeContent?.capabilities || DEFAULT_CAPABILITIES;
    const aboutTeaser = homeContent?.aboutTeaser || DEFAULT_ABOUT_TEASER;
    const selectedWork = (projects || []).slice(0, 3).map((project) => ({
        year: project.year || '',
        company: project.company || '',
        title: project.title || '',
        summary: project.description || '',
        tags: Array.isArray(project.tags) ? project.tags : [],
        href: project.link || '/work',
    }));
    const contactBand = {
        ...DEFAULT_CONTACT_BAND,
        ...(homeContent?.contactBand || {}),
        ...(contactBandData || {}),
    };

    return (
        <>
            <Hero hero={hero} />
            <Work work={work} selectedWork={selectedWork} />
            <Capabilities capabilitiesSection={capabilitiesSection} capabilities={capabilities} />
            <AboutTeaser aboutTeaser={aboutTeaser} />
            <ContactBand contactBand={contactBand} />
        </>
    );
}

function Hero({ hero }) {
    return (
        <section className="container-page pt-10 pb-12 md:pt-24 md:pb-28">
            <div className="grid grid-cols-12 gap-10 md:gap-12 items-center">
                {/* Left: copy */}
                <div className="col-span-12 md:col-span-8">
                    <p className="eyebrow mb-6 fade-up">
                        <span className="inline-block w-2 h-2 rounded-full bg-accent mr-2 align-middle" />
                        {hero.eyebrow}
                    </p>
                    <h1 className="font-display text-[40px] md:text-[64px] leading-[1.04] tracking-tightest text-fg max-w-[16ch] fade-up">
                        {hero.heading}
                    </h1>
                    <p className="mt-7 max-w-prose text-[16px] md:text-[17px] text-fg-muted leading-relaxed fade-up">
                        {hero.description}
                    </p>
                    <div className="mt-9 flex items-center gap-3 fade-up">
                        <Link to="/work" className="btn btn-primary">
                            See selected work
                            <Arrow />
                        </Link>
                        <Link to="/contact" className="btn btn-ghost">
                            Get in touch
                        </Link>
                    </div>
                </div>

                {/* Right: circular avatar */}
                <div className="hidden md:flex col-span-12 md:col-span-4 fade-up md:justify-end">
                    <figure className="relative w-[140px] h-[140px] md:w-[240px] md:h-[240px]">
                        {/* subtle offset accent ring behind avatar */}
                        <div
                            className="absolute inset-0 translate-x-3 translate-y-3 rounded-full bg-accent-soft -z-10"
                            aria-hidden="true"
                        />
                        <img
                            src="/images/chaw-face.jpg"
                            alt="Chaw Thinn"
                            width="720"
                            height="720"
                            loading="eager"
                            fetchpriority="high"
                            className="relative w-full h-full rounded-full object-cover border border-line bg-elevated"
                        />
                    </figure>
                </div>
            </div>
        </section>
    );
}

function Work({ work, selectedWork }) {
    return (
        <section id="work" className="border-t border-line">
            <div className="container-page py-12 md:py-28">
                <div className="flex items-end justify-between mb-8 md:mb-16">
                    <div>
                        <p className="eyebrow mb-3">{work.eyebrow}</p>
                        <h2 className="font-display text-3xl md:text-5xl tracking-tight">
                            {work.heading}
                        </h2>
                    </div>
                    <Link to="/work" className="hidden md:inline-flex link-underline text-[14px]">
                        All case studies
                        <Arrow />
                    </Link>
                </div>

                <ul className="divide-y divide-line border-y border-line">
                    {selectedWork.map((item) => (
                        <li key={item.title}>
                            <Link
                                to="/work"
                                className="group grid grid-cols-12 gap-6 py-7 md:py-9 items-start transition-colors duration-300 hover:bg-elevated px-2 md:px-4 -mx-2 md:-mx-4 rounded-md"
                            >
                                <div className="col-span-12 md:col-span-2 flex md:block items-baseline gap-3">
                                    <span className="font-mono text-[12px] text-fg-soft">{item.year}</span>
                                    <span className="block text-[13px] text-fg-muted md:mt-1">{item.company}</span>
                                </div>
                                <div className="col-span-12 md:col-span-7">
                                    <h3 className="font-display text-[20px] md:text-[24px] leading-snug tracking-tight text-fg group-hover:text-accent transition-colors">
                                        {item.title}
                                    </h3>
                                    <p className="mt-3 text-[15px] text-fg-muted max-w-prose">
                                        {item.summary}
                                    </p>
                                </div>
                                <div className="col-span-12 md:col-span-3 flex flex-wrap gap-2 md:justify-end">
                                    {item.tags.map((t) => (
                                        <span key={t} className="pill">{t}</span>
                                    ))}
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>

                <div className="md:hidden mt-8">
                    <Link to="/work" className="link-underline text-[14px]">
                        All case studies
                        <Arrow />
                    </Link>
                </div>
            </div>
        </section>
    );
}

function Capabilities({ capabilitiesSection, capabilities }) {
    return (
        <section className="border-t border-line bg-elevated">
            <div className="container-page py-12 md:py-28">
                <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-4">
                        <p className="eyebrow mb-3">{capabilitiesSection.eyebrow}</p>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight max-w-[14ch]">
                            {capabilitiesSection.heading}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-10">
                        {capabilities.map((c, i) => (
                            <div key={c.label}>
                                <p className="font-mono text-[11px] text-fg-soft mb-2">
                                    0{i + 1}
                                </p>
                                <h3 className="font-display text-[18px] tracking-tight mb-2">
                                    {c.label}
                                </h3>
                                <p className="text-[14px] text-fg-muted leading-relaxed">
                                    {c.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function AboutTeaser({ aboutTeaser }) {
    return (
        <section className="border-t border-line">
            <div className="container-page py-12 md:py-28 grid grid-cols-12 gap-8">
                <div className="col-span-12 md:col-span-4">
                    <p className="eyebrow">{aboutTeaser.eyebrow}</p>
                </div>
                <div className="col-span-12 md:col-span-8">
                    <p className="font-display text-[24px] md:text-[34px] leading-[1.25] tracking-tight text-fg max-w-[34ch]">
                        {aboutTeaser.description}
                    </p>
                    <div className="mt-8">
                        <Link to="/about" className="link-underline text-[14px]">
                            More about me
                            <Arrow />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ContactBand({ contactBand }) {
    return (
        <section className="border-t border-line">
            <div className="container-page py-14 md:py-32 text-center">
                <p className="eyebrow mb-6">{contactBand.eyebrow}</p>
                <h2 className="font-display text-4xl md:text-6xl tracking-tightest leading-[1.05] max-w-[18ch] mx-auto">
                    {contactBand.heading}
                </h2>
                <div className="mt-10 flex items-center justify-center gap-3">
                    <a href={`mailto:${contactBand.email}`} className="btn btn-primary">
                        Email me
                        <Arrow />
                    </a>
                    <a
                        href={contactBand.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost"
                    >
                        LinkedIn
                    </a>
                </div>
            </div>
        </section>
    );
}

function Arrow() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
    );
}
